import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import PageLoader from '@/components/layouts/PageLoader';
import { useSiteWaterLevel } from '@/hooks/useSiteWaterLevel';

import type { WaterLevelPeriod } from '@/remote/dashboard/water-level-chart';
import type { WaterLevelChartData } from '@/types/site';
import { addDays, format } from 'date-fns';

type MinuteInterval = 1 | 5 | 10 | 30 | 60;

interface WaterLevelChartProps {
  readonly siteId: string;
  readonly initialPeriod?: WaterLevelPeriod;
  readonly isFullScreen?: boolean;
}

interface TimePeriodButtonsProps {
  readonly selectedPeriod: WaterLevelPeriod;
  readonly onPeriodChange: (period: WaterLevelPeriod) => void;
  readonly selectedDate: Date | null;
  readonly setSelectedDate: (date: Date | null) => void;
  readonly isFullScreen: boolean;
}

interface IntervalButtonsProps {
  readonly selectedInterval: MinuteInterval;
  readonly onIntervalChange: (interval: MinuteInterval) => void;
}

const IntervalButtons = ({
  selectedInterval,
  onIntervalChange,
}: IntervalButtonsProps) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-gray-600 whitespace-nowrap">분 간격:</span>
    {[
      { value: 1 as const, label: '1분' },
      { value: 5 as const, label: '5분' },
      { value: 10 as const, label: '10분' },
      { value: 30 as const, label: '30분' },
      { value: 60 as const, label: '1시간' },
    ].map(({ value, label }) => (
      <button
        key={value}
        onClick={() => onIntervalChange(value)}
        className={`px-3 py-1 rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
          selectedInterval === value
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const TimePeriodButtons = ({
  selectedPeriod,
  onPeriodChange,
  selectedDate,
  setSelectedDate,
  isFullScreen = false,
}: TimePeriodButtonsProps) => (
  <div
    className={`flex  ${isFullScreen ? 'justify-between' : 'flex-col gap-2 justify-center items-center'}`}
  >
    <div className="flex items-center gap-4">
      {[
        { key: 'daily' as const, label: '일간' },
        { key: 'weekly' as const, label: '주간' },
        { key: 'monthly' as const, label: '월간' },
      ].map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onPeriodChange(key)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
            selectedPeriod === key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
    {selectedPeriod === 'daily' && (
      <div className="flex items-center space-x-2 ">
        <button
          onClick={() =>
            setSelectedDate(selectedDate ? addDays(selectedDate, -1) : null)
          }
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <input
          type="date"
          value={format(selectedDate ?? new Date(), 'yyyy-MM-dd')}
          onChange={(e) => {
            const newDate = new Date(e.target.value);
            console.log('날짜 변경:', e.target.value, '→', newDate);
            setSelectedDate(newDate);
          }}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center space-x-2 min-w-[160px]"
          style={{
            colorScheme: 'light',
          }}
        />
        <button
          onClick={() =>
            setSelectedDate(selectedDate ? addDays(selectedDate, 1) : null)
          }
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    )}
  </div>
);

interface ChartContentProps {
  readonly data: WaterLevelChartData[];
  readonly period: WaterLevelPeriod;
  readonly isFullScreen: boolean;
  readonly selectedDate: Date | null;
  readonly minuteInterval: MinuteInterval;
}

const ChartContent = ({
  data,
  period,
  isFullScreen,
  selectedDate,
  minuteInterval,
}: ChartContentProps) => {
  const chartRef = useRef<ReactECharts>(null);

  // isFullScreen 변화에 따른 차트 리사이즈
  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        const echartInstance = chartRef.current.getEchartsInstance();
        if (echartInstance) {
          echartInstance.resize({
            animation: {
              duration: 300,
              easing: 'cubicInOut',
            },
          });
        }
      }
    }, 100); // 애니메이션 완료를 위한 약간의 지연

    return () => clearTimeout(timer);
  }, [isFullScreen]);

  // 컨테이너 크기 변화 감지 (ResizeObserver + window resize)
  useEffect(() => {
    const resizeChart = () => {
      if (chartRef.current) {
        const echartInstance = chartRef.current.getEchartsInstance();
        if (echartInstance) {
          echartInstance.resize();
        }
      }
    };

    // 윈도우 리사이즈 이벤트
    const handleWindowResize = () => {
      requestAnimationFrame(resizeChart);
    };

    window.addEventListener('resize', handleWindowResize);

    // ResizeObserver로 컨테이너 크기 변화 감지
    let resizeObserver: ResizeObserver | null = null;

    if (chartRef.current && 'ResizeObserver' in window) {
      const chartContainer = chartRef.current.ele;
      if (chartContainer) {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            // 크기가 실제로 변했을 때만 리사이즈
            if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
              requestAnimationFrame(resizeChart);
            }
          }
        });
        resizeObserver.observe(chartContainer);
      }
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // 차트 초기 렌더링 완료 후 리사이즈 (브라우저 렌더링 완료 대기)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        const echartInstance = chartRef.current.getEchartsInstance();
        if (echartInstance) {
          echartInstance.resize();
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [data]);

  const chartOption = useMemo((): EChartsOption => {
    // 데이터의 최대값 계산
    const maxWaterLevel = Math.max(
      ...data.map((item) => item.waterLevel ?? 0),
      100 // 최소 100은 유지
    );
    // 10 단위로 올림 (101이면 110, 112면 120)
    const yAxisMax = Math.ceil(maxWaterLevel / 10) * 10;
    // interval도 동적으로 계산 (4등분하여 10 단위로 올림)
    const yAxisInterval = Math.ceil(yAxisMax / 4 / 10) * 10;

    const getChartTitle = () => {
      switch (period) {
        case 'daily':
          return (
            `일간 수위 추이 (${selectedDate ? format(selectedDate, 'M. dd / ') + format(addDays(selectedDate, -1), 'M. dd') : '어제/오늘'})` +
            `, ${minuteInterval}분 간격`
          );
        case 'weekly':
          return `주간 수위 추이 (최근 7일) ${selectedDate ? format(selectedDate, 'yyyy년 MM월 dd일') : ''}`;
        case 'monthly':
          return `월간 수위 추이 (최근 30일) ${selectedDate ? format(selectedDate, 'yyyy년 MM월 dd일') : ''}`;
        default:
          return '수위 추이';
      }
    };

    const formatTimeLabel = (timeStr: string, period: WaterLevelPeriod) => {
      if (period === 'daily') {
        const timePart = timeStr.split(' ')[1];
        if (timePart) {
          const hour = parseInt(timePart.split(':')[0]);
          return `${hour}시`;
        }
      } else {
        const datePart = timeStr.split('-');
        if (datePart.length === 3) {
          const month = parseInt(datePart[1]);
          const day = parseInt(datePart[2]);
          return `${month}/${day}`;
        }
      }
      return timeStr;
    };

    // Daily 차트인 경우 어제/오늘 구분
    const isDailyChart = period === 'daily';

    let seriesData: Array<{
      value: [number, number | null];
      name: string;
      itemStyle?: { color: string };
    }>;
    let xAxisData: string[];
    let yesterdayData: Array<[number, number | null]> = [];
    let todayData: Array<[number, number | null]> = [];

    if (isDailyChart) {
      // Daily 차트의 경우 선택한 날짜와 이전 날짜 데이터를 분리하여 겹치도록 표시
      const midPoint = Math.floor(data.length / 2);
      const yesterdayItems = data.slice(0, midPoint);
      const todayItems = data.slice(midPoint);

      // 동적 분 간격 시간 라벨 생성 (0시 0분부터 23시 59분까지)
      const totalPoints = Math.floor(1440 / minuteInterval); // 총 포인트 수
      const timeLabels = Array.from({ length: totalPoints }, (_, i) => {
        const totalMinutes = i * minuteInterval;
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      });

      // 어제 데이터 매핑 - 동적 분 간격으로 모든 시간대에 대해 데이터 매핑
      yesterdayData = timeLabels.map((_, timeIndex) => {
        const totalMinutes = timeIndex * minuteInterval;
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        const item = yesterdayItems.find((item) => {
          const timePart = item.time.split(' ')[1];
          if (timePart) {
            const [itemHour, itemMinute] = timePart.split(':').map(Number);
            return itemHour === hour && itemMinute === minute;
          }
          return false;
        });
        return item ? [timeIndex, item.waterLevel] : [timeIndex, null];
      });

      // 오늘 데이터 매핑 - 동적 분 간격으로 모든 시간대에 대해 데이터 매핑
      todayData = timeLabels.map((_, timeIndex) => {
        const totalMinutes = timeIndex * minuteInterval;
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        const item = todayItems.find((item) => {
          const timePart = item.time.split(' ')[1];
          if (timePart) {
            const [itemHour, itemMinute] = timePart.split(':').map(Number);
            return itemHour === hour && itemMinute === minute;
          }
          return false;
        });
        return item ? [timeIndex, item.waterLevel] : [timeIndex, null];
      });

      // x축 데이터 설정 - 동적 분 간격으로 표시
      xAxisData = timeLabels.map((label, index) => {
        const step = Math.max(
          1,
          Math.ceil(totalPoints / (isFullScreen ? 24 : 12))
        );
        return index % step === 0 ? label : '';
      });

      seriesData = []; // Daily의 경우 series에서 별도 처리
    } else {
      // 기존 로직 (주간/월간)
      seriesData = data.map((item, index) => ({
        value: [index, item.waterLevel],
        name: item.time,
        itemStyle: {
          color: '#2563EB',
        },
      }));

      xAxisData = data.map((item, index) => {
        const label = formatTimeLabel(item.time, period);
        const step = Math.max(
          1,
          Math.ceil(data.length / (isFullScreen ? 12 : 6))
        );
        return index % step === 0 ? label : '';
      });
    }

    const option: EChartsOption = {
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
      title: {
        text: getChartTitle(),
        left: 'center',
        textStyle: {
          fontSize: isFullScreen ? 18 : 14,
          fontWeight: 'bold',
          color: '#1F2937',
        },
      },
      legend: isDailyChart
        ? {
            data: selectedDate
              ? [
                  format(addDays(selectedDate, -1), 'MM월 dd일'),
                  format(selectedDate, 'MM월 dd일'),
                ]
              : ['어제', '오늘'],
            bottom: '1',
            itemGap: 20,
            textStyle: {
              color: '#6B7280',
              fontSize: isFullScreen ? 14 : 12,
            },
            icon: 'block',
          }
        : undefined,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        textStyle: {
          color: '#374151',
        },
        formatter: (params: unknown) => {
          const paramArray = Array.isArray(params) ? params : [params];

          if (isDailyChart) {
            // Daily 차트의 경우 여러 시리즈 표시
            const timeIndex = paramArray[0]?.dataIndex;
            const totalMinutes = timeIndex * minuteInterval;
            const timeLabel = `${Math.floor(totalMinutes / 60)
              .toString()
              .padStart(
                2,
                '0'
              )}:${(totalMinutes % 60).toString().padStart(2, '0')}`; // 동적 분 간격 시간 라벨 사용

            let content = `<div style="padding: 8px;"><div style="font-weight: bold; margin-bottom: 8px;">${timeLabel}</div>`;

            paramArray.forEach(
              (param: {
                value: [number, number | null];
                seriesName: string;
                color: string;
              }) => {
                const value = param.value[1];
                const seriesName = param.seriesName;
                const color = param.color;

                if (value !== null) {
                  content += `
                  <div style="display: flex; align-items: center; margin-bottom: 4px;">
                    <div style="width: 12px; height: 12px; background-color: ${color}; border-radius: 50%; margin-right: 8px;"></div>
                    <span>${seriesName}: <strong>${value}%</strong></span>
                  </div>
                `;
                } else {
                  content += `
                  <div style="display: flex; align-items: center; margin-bottom: 4px;">
                    <div style="width: 12px; height: 12px; border: 2px dashed ${color}; border-radius: 50%; margin-right: 8px;"></div>
                    <span>${seriesName}: <span style="color: #9CA3AF;">데이터 없음</span></span>
                  </div>
                `;
                }
              }
            );

            content += '</div>';
            return content;
          } else {
            // 기존 로직 (주간/월간)
            const param = paramArray[0] as { dataIndex: number; color: string };
            const dataIndex = param?.dataIndex;
            const item = data[dataIndex];

            if (!item) {
              return `
                <div style="padding: 8px;">
                  <div style="color: #9CA3AF;">데이터 없음</div>
                </div>
              `;
            }

            if (item.waterLevel === null) {
              return `
                <div style="padding: 8px;">
                  <div style="font-weight: bold; margin-bottom: 4px;">${item.time}</div>
                  <div style="color: #9CA3AF;">데이터 없음</div>
                </div>
              `;
            }

            return `
              <div style="padding: 8px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${item.time}</div>
                <div style="display: flex; align-items: center;">
                  <div style="width: 12px; height: 12px; background-color: ${param.color}; border-radius: 50%; margin-right: 8px;"></div>
                  <span>수위: <strong>${item.waterLevel}%</strong></span>
                </div>
              </div>
            `;
          }
        },
      },
      grid: {
        left: isFullScreen ? '8%' : '12%',
        right: isFullScreen ? '8%' : '12%',
        top: isDailyChart
          ? isFullScreen
            ? '22%'
            : '32%' // 축소 시 더 많은 공간 확보
          : isFullScreen
            ? '18%'
            : '22%',
        bottom: isFullScreen ? '12%' : '18%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: {
          color: '#9CA3AF',
          fontSize: isFullScreen ? 12 : 10,
          rotate: period === 'daily' ? 0 : 45,
        },
        axisLine: {
          lineStyle: {
            color: '#E5E7EB',
          },
        },
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#E5E7EB',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: '수위 (%)',
        nameTextStyle: {
          color: '#6B7280',
          fontSize: isFullScreen ? 14 : 12,
        },
        min: 0,
        max: yAxisMax,
        interval: yAxisInterval,
        axisLabel: {
          color: '#9CA3AF',
          fontSize: isFullScreen ? 12 : 10,
          formatter: '{value}%',
        },
        axisLine: {
          lineStyle: {
            color: '#E5E7EB',
          },
        },
        axisTick: {
          lineStyle: {
            color: '#E5E7EB',
          },
        },
        splitLine: {
          lineStyle: {
            color: '#F3F4F6',
            type: 'solid',
          },
        },
      },
      series: isDailyChart
        ? [
            // 어제 데이터 시리즈
            {
              name: selectedDate
                ? format(addDays(selectedDate, -1), 'MM월 dd일')
                : '어제',
              type: 'line',
              data: yesterdayData,
              smooth: true,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: isFullScreen ? 8 : 6,
              lineStyle: {
                width: isFullScreen ? 3 : 2,
                color: '#F97316', // 주황색
              },
              itemStyle: {
                color: '#F97316',
                borderWidth: 2,
                borderColor: '#FFFFFF',
              },
              emphasis: {
                itemStyle: {
                  borderWidth: 3,
                  shadowBlur: 10,
                  shadowColor: 'rgba(249, 115, 22, 0.3)',
                },
              },
              animationDuration: 1000,
              animationEasing: 'cubicOut',
            },
            // 오늘 데이터 시리즈
            {
              name: selectedDate ? format(selectedDate, 'MM월 dd일') : '오늘',
              type: 'line',
              data: todayData,
              smooth: true,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: isFullScreen ? 8 : 6,
              lineStyle: {
                width: isFullScreen ? 3 : 2,
                color: '#2563EB', // 파란색
              },
              itemStyle: {
                color: '#2563EB',
                borderWidth: 2,
                borderColor: '#FFFFFF',
              },
              emphasis: {
                itemStyle: {
                  borderWidth: 3,
                  shadowBlur: 10,
                  shadowColor: 'rgba(37, 99, 235, 0.3)',
                },
              },
              animationDuration: 1000,
              animationEasing: 'cubicOut',
            },
          ]
        : [
            // 기존 로직 (주간/월간)
            {
              name: '수위',
              type: 'line',
              data: seriesData,
              smooth: true,
              connectNulls: true,
              symbol: 'circle',
              symbolSize: isFullScreen ? 8 : 6,
              lineStyle: {
                width: isFullScreen ? 3 : 2,
                color: '#2563EB',
              },
              itemStyle: {
                borderWidth: 2,
                borderColor: '#FFFFFF',
              },
              emphasis: {
                itemStyle: {
                  borderWidth: 3,
                  shadowBlur: 10,
                  shadowColor: 'rgba(37, 99, 235, 0.3)',
                },
              },
              markPoint: {
                data: data
                  .map((item, index) =>
                    item.waterLevel === null
                      ? {
                          name: '데이터 없음',
                          coord: [index, 50],
                          symbol: 'circle',
                          symbolSize: isFullScreen ? 8 : 6,
                          itemStyle: {
                            color: 'transparent',
                            borderColor: '#9CA3AF',
                            borderWidth: 2,
                            borderType: 'dashed' as const,
                          },
                          label: {
                            show: false,
                          },
                        }
                      : null
                  )
                  .filter(
                    (item): item is NonNullable<typeof item> => item !== null
                  ),
              },
              animationDuration: 1000,
              animationEasing: 'cubicOut',
            },
          ],
      // 줌 기능 (풀스크린일 때만)
      dataZoom: isFullScreen
        ? [
            {
              type: 'slider',
              show: true,
              xAxisIndex: [0],
              start: 0,
              end: 100,
              height: 30,
              bottom: 10,
              textStyle: {
                fontSize: 12,
              },
            },
            {
              type: 'inside',
              xAxisIndex: [0],
            },
          ]
        : [],
      toolbox: isFullScreen
        ? {
            feature: {
              saveAsImage: {
                title: '이미지로 저장',
                name: `수위차트_${period}`,
              },
              restore: {
                title: '초기화',
              },
              dataZoom: {
                title: {
                  zoom: '줌',
                  back: '되돌리기',
                },
              },
            },
            right: 20,
            top: 20,
          }
        : undefined,
    };

    return option;
  }, [data, period, isFullScreen, selectedDate, minuteInterval]);

  return (
    <div className="w-full">
      <div
        className={`${
          isFullScreen ? 'h-[400px]' : 'h-[240px]'
        } relative overflow-hidden`}
        style={{ minWidth: '300px' }}
      >
        <ReactECharts
          ref={chartRef}
          option={chartOption}
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          opts={{
            renderer: 'canvas',
            locale: 'ko',
          }}
          notMerge={false}
          lazyUpdate={false}
          theme="light"
          onEvents={{}}
          shouldSetOption={() => true}
        />
      </div>

      {/* 범례 (Daily 차트일 때 데이터 없음 표시) */}
      {period === 'daily' && (
        <div className="flex items-center justify-center mt-4 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-gray-400 border-dashed rounded-full"></div>
            <span>데이터 없음</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function WaterLevelChart({
  siteId,
  initialPeriod = 'daily',
  isFullScreen = false,
}: WaterLevelChartProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [minuteInterval, setMinuteInterval] = useState<MinuteInterval>(1);

  const formattedDate = selectedDate
    ? format(selectedDate, 'yyyy-MM-dd')
    : undefined;
  console.log(
    'WaterLevelChart - selectedDate:',
    selectedDate,
    'formattedDate:',
    formattedDate
  );

  const { data, isLoading, isError, error, period, actions } =
    useSiteWaterLevel(siteId, initialPeriod, {}, formattedDate);

  const containerClass = isFullScreen
    ? 'bg-white rounded-xl p-8 shadow-lg'
    : 'bg-gray-50 rounded-xl p-6 sm:bg-white sm:rounded-xl sm:p-8 sm:shadow-lg border-b border-gray-200';

  if (isLoading) {
    return (
      <div className={containerClass}>
        <div className="mb-4">
          <TimePeriodButtons
            selectedPeriod={period}
            onPeriodChange={actions.setPeriod}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            isFullScreen={isFullScreen}
          />
        </div>
        <div
          className={`flex items-center justify-center text-gray-500 ${
            isFullScreen ? 'h-[400px]' : 'h-[240px]'
          }`}
        >
          <div className="text-center">
            <PageLoader />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={containerClass}>
        <div className="mb-4">
          <TimePeriodButtons
            selectedPeriod={period}
            onPeriodChange={actions.setPeriod}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            isFullScreen={isFullScreen}
          />
          {period === 'daily' && (
            <div className="mt-3">
              <IntervalButtons
                selectedInterval={minuteInterval}
                onIntervalChange={setMinuteInterval}
              />
            </div>
          )}
        </div>
        <div
          className={`flex items-center justify-center text-red-500 ${
            isFullScreen ? 'h-[400px]' : 'h-[240px]'
          }`}
        >
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl mb-2"></i>
            <p>차트 데이터를 불러올 수 없습니다</p>
            {error && (
              <p className="text-sm mt-1 text-gray-600">{error.message}</p>
            )}
            <button
              onClick={actions.refetch}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={containerClass}>
        <div className="mb-4">
          <TimePeriodButtons
            selectedPeriod={period}
            onPeriodChange={actions.setPeriod}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            isFullScreen={isFullScreen}
          />
          {period === 'daily' && (
            <div className="mt-3">
              <IntervalButtons
                selectedInterval={minuteInterval}
                onIntervalChange={setMinuteInterval}
              />
            </div>
          )}
        </div>
        <div
          className={`flex items-center justify-center text-gray-500 ${
            isFullScreen ? 'h-[400px]' : 'h-[240px]'
          }`}
        >
          <div className="text-center">
            <i className="ri-line-chart-line text-4xl mb-2"></i>
            <p>차트 데이터가 없습니다</p>
            <p className="text-xs mt-2">다른 기간을 선택해보세요</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className="mb-4">
        <TimePeriodButtons
          isFullScreen={isFullScreen}
          selectedPeriod={period}
          onPeriodChange={actions.setPeriod}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        {period === 'daily' && (
          <div className="mt-3">
            <IntervalButtons
              selectedInterval={minuteInterval}
              onIntervalChange={setMinuteInterval}
            />
          </div>
        )}
      </div>

      <ChartContent
        selectedDate={selectedDate}
        data={data}
        period={period}
        isFullScreen={isFullScreen}
        minuteInterval={minuteInterval}
      />
    </div>
  );
}
